import { applyTheme, isTheme } from "../lib/theme";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { authApi } from "../api/auth";
import { onUnauthorized } from "../api/client";
import { configApi } from "../api/resources";
import type { PublicConfig, SessionInfo } from "../api/types";

interface AuthState {
  loading: boolean;
  session: SessionInfo | null;
  config: PublicConfig | null;
  refresh: () => Promise<SessionInfo | null>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [config, setConfig] = useState<PublicConfig | null>(null);

  const refresh = useCallback(async () => {
    try {
      const s = await authApi.session();
      setSession(s);
      if (s.authenticated && isTheme(s.ui_theme)) applyTheme(s.ui_theme, false); // server preference wins after sign-in
      return s;
    } catch {
      setSession(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      await refresh();
    }
  }, [refresh]);

  useEffect(() => {
    void refresh();
    configApi.publicConfig().then(setConfig).catch(() => setConfig(null));
    return onUnauthorized(() => void refresh());
  }, [refresh]);

  const value = useMemo(() => ({ loading, session, config, refresh, logout }), [loading, session, config, refresh, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
